import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Star, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { DevistoLogo } from "@/components/DevistoLogo";
import { apiError, beDate, btnPrimary, inputCls } from "@/lib/studioApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = ["", "Décevant", "Moyen", "Bien", "Très bien", "Excellent"];

export default function PublicReview() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(Number(params.get("note")) || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/avis/${token}`).then(({ data: d }) => { setData(d); if (d.review) setRating(d.review.rating); }).catch((e) => setError(apiError(e)));
  }, [token]);

  const submit = async () => {
    if (!rating) return toast.error("Choisissez une note.");
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/avis/${token}`, { rating, comment });
      setData(d);
      toast.success("Merci pour votre avis !");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (error) return <main className="min-h-screen bg-[#F3F5FC] p-6 text-center text-sm text-rose-700" data-testid="public-review-error">{error}</main>;
  if (!data) return <main className="min-h-screen bg-[#F3F5FC] p-10 text-center text-sm text-[#5C6A87]">Chargement…</main>;
  const company = data.company || {};
  const done = !!data.review;
  const showGoogle = done && data.review.rating >= 4 && data.google_url;

  return (
    <main className="min-h-screen bg-[#F3F5FC] px-4 py-8 font-body sm:py-14">
      <div className="mx-auto max-w-lg">
        <div className="rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)] sm:p-8" data-testid="public-review">
          <div className="flex items-center gap-3">
            {company.logo_media_id ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${company.logo_media_id}`} alt="" className="h-10 w-auto" /> : null}
            <div>
              <p className="font-heading text-base font-bold text-[#0F2645]">{company.name}</p>
              <p className="text-xs text-[#5C6A87]">Intervention {data.reference}{data.date ? ` · ${beDate(data.date)}` : ""}</p>
            </div>
          </div>
          <h1 className="mt-6 font-heading text-2xl font-bold text-[#0F2645]">{done ? "Merci pour votre avis !" : "Comment s'est passée notre intervention ?"}</h1>
          <p className="mt-1 text-sm text-[#5C6A87]">{done ? "Votre retour a bien été transmis à l'équipe." : "Une minute suffit, cela nous aide énormément."}</p>

          <div className="mt-6 flex items-center gap-1" data-testid="public-review-stars" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" disabled={done} data-testid={`public-review-star-${n}`} onClick={() => setRating(n)} onMouseEnter={() => setHover(n)}
                className="rounded-md p-1 transition-transform duration-100 hover:scale-110 disabled:cursor-default disabled:hover:scale-100" aria-label={`${n} étoiles`}>
                <Star className={`h-9 w-9 ${(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-[#C9D4F4]"}`} strokeWidth={1.8} />
              </button>
            ))}
            <span className="ml-2 text-sm font-semibold text-[#0F2645]" data-testid="public-review-label">{LABELS[hover || rating] || ""}</span>
          </div>

          {!done && (
            <>
              <textarea data-testid="public-review-comment" rows={3} className={`${inputCls} mt-5`} placeholder={rating && rating <= 3 ? "Dites-nous ce que nous pouvons améliorer (lu uniquement par l'entreprise)" : "Un mot sur l'intervention ? (facultatif)"} value={comment} onChange={(e) => setComment(e.target.value)} />
              <button type="button" data-testid="public-review-submit" disabled={busy || !rating} onClick={submit} className={`${btnPrimary} mt-4 w-full`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Envoyer mon avis
              </button>
            </>
          )}

          {showGoogle && (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="public-review-google">
              <p className="text-sm font-semibold text-[#0F2645]">Un grand merci ! Pourriez-vous partager votre expérience publiquement ?</p>
              <p className="mt-1 text-xs text-[#5C6A87]">Votre avis Google aide d'autres clients à nous faire confiance.</p>
              <a href={data.google_url} target="_blank" rel="noreferrer" data-testid="public-review-google-link" className={`${btnPrimary} mt-3 bg-[#0F2645] hover:bg-[#0A1B33]`}>
                <ExternalLink className="h-4 w-4" /> Laisser un avis Google
              </a>
            </div>
          )}
          {done && !showGoogle && data.review.rating <= 3 && (
            <p className="mt-5 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4 text-sm text-[#0F2645]" data-testid="public-review-sorry">Nous sommes désolés que l'intervention n'ait pas été à la hauteur. {company.name} vous recontactera rapidement{company.phone ? ` — ${company.phone}` : ""}.</p>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-[#93A0BC]">Propulsé par <DevistoLogo className="inline h-4 w-auto opacity-60" /></p>
      </div>
    </main>
  );
}
